import React from "react";
import { ChevronRight } from "lucide-react";
import { Lien } from "./Lien";

/* Le petit vocabulaire visuel de l'application, celui des réglages d'un iPhone : des
   listes groupées sur fond blanc, une icône colorée à gauche, une flèche à droite.
   Chaque écran parle la même langue — on ne réapprend rien d'un onglet à l'autre. */

/** Titre de section, au-dessus d'un groupe. */
export const TitreGroupe = ({ children, action }) => (
  <div className="flex items-end justify-between gap-3 px-1 mb-2">
    <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--muted)]">
      {children}
    </h2>
    {action}
  </div>
);

/** Un bloc blanc arrondi dont les lignes sont séparées par un filet fin. */
export const Groupe = ({ children, className = "", testId, style }) => (
  <div className={`rounded-[22px] bg-white overflow-hidden divide-y ${className}`}
    style={{ border: "1px solid var(--border)", borderColor: "var(--border)", ...style }}
    data-testid={testId}>
    {children}
  </div>
);

/** La pastille d'icône, couleur pleine comme dans les réglages iOS. */
export const Pastille = ({ Icone, couleur = "var(--primary)", taille = 30 }) => (
  <span className="grid place-items-center shrink-0 text-white"
    style={{ width: taille, height: taille, borderRadius: taille * 0.3, background: couleur }}>
    <Icone size={Math.round(taille * 0.55)} strokeWidth={2.2} />
  </span>
);

/** Une ligne touchable : icône, texte, éventuel badge, flèche. Bouton ou lien. */
export const Ligne = ({ Icone, couleur, titre, sousTitre, badge, onClick, href, testId,
  fleche = true, ouvert = false, danger = false }) => {
  const contenu = (
    <>
      {Icone && <Pastille Icone={Icone} couleur={couleur} />}
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-[15px] leading-snug"
          style={{ color: danger ? "#DC2626" : "var(--text)" }}>{titre}</span>
        {sousTitre && (
          <span className="block text-[12.5px] text-[var(--muted)] leading-snug mt-0.5">{sousTitre}</span>
        )}
      </span>
      {badge}
      {fleche && (
        <ChevronRight size={18} className="shrink-0 transition-transform"
          style={{ color: "#C4C9D0", transform: ouvert ? "rotate(90deg)" : undefined }} />
      )}
    </>
  );
  const classes = "w-full flex items-center gap-3 px-4 py-3 min-h-[52px] active:bg-[#F1F5F9] transition-colors";
  if (href) {
    return <Lien href={href} className={classes} data-testid={testId}>{contenu}</Lien>;
  }
  return (
    <button type="button" onClick={onClick} className={classes} data-testid={testId}
      aria-expanded={fleche && onClick ? ouvert : undefined}>
      {contenu}
    </button>
  );
};

/** Un petit badge rond (« À compléter », « 1 »). */
export const Badge = ({ children, couleur = "#DC2626" }) => (
  <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 shrink-0 text-white max-w-full break-words"
    style={{ background: couleur }}>{children}</span>
);

/** Des segments comme sur iPhone (« À traiter | Messages »). */
export const Segments = ({ options, valeur, onChange, testId }) => (
  <div className="flex p-1 rounded-2xl gap-1" style={{ background: "#E2E8F0" }} data-testid={testId}>
    {options.map((o) => {
      const actif = o.id === valeur;
      return (
        <button key={o.id} onClick={() => onChange(o.id)} data-testid={`${testId}-${o.id}`}
          className="flex-1 min-h-[38px] rounded-xl text-[14px] font-semibold px-2 transition-all"
          style={actif
            ? { background: "#fff", color: "var(--text)", boxShadow: "0 1px 3px rgba(15,27,45,0.15)" }
            : { color: "#475569" }}>
          {o.label}
        </button>
      );
    })}
  </div>
);

/** Le bouton « ‹ Retour » d'un écran de détail. */
export const Retour = ({ onClick, label = "Retour" }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-[15px] font-medium min-h-[40px] -ml-1"
    style={{ color: "var(--primary)" }} data-testid="ecran-retour">
    <ChevronRight size={18} style={{ transform: "rotate(180deg)" }} /> {label}
  </button>
);

/** Une carte d'état vide ou d'erreur, centrée. */
export const Vide = ({ children }) => (
  <div className="rounded-[22px] bg-white px-5 py-8 text-center text-sm text-[var(--muted)]"
    style={{ border: "1px solid var(--border)" }}>{children}</div>
);
